import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

const GooglePlaceField = ({ company, field, value, onChange, error, disabled }) => {
  const [inputValue, setInputValue] = useState('');
  const [postalError, setPostalError] = useState('');
  const [filteredPredictions, setFilteredPredictions] = useState([]); // suburbs suggestions
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const suburbsCache = useRef(null); // loaded JSON
  const [loadErrorMsg, setLoadErrorMsg] = useState('');

  // Load suburbs JSON from public folder (lazy)
  const ensureSuburbsLoaded = useCallback(async () => {
    if (suburbsCache.current) return suburbsCache.current;
    try {
      const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
      const url = `${base}/data/au-suburbs.json`;
      const res = await fetch(url, { cache: 'force-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      suburbsCache.current = Array.isArray(data.data) ? data.data : [];
      if (!Array.isArray(data.data) || data.data.length === 0) {
        setLoadErrorMsg('Suburbs list is empty or invalid.');
      } else {
        setLoadErrorMsg('');
      }
    } catch (e) {
      console.error('Failed to load suburbs JSON', e);
      suburbsCache.current = [];
      setLoadErrorMsg('Failed to load suburbs data. Please check public/data/au-suburbs.json');
    }
    return suburbsCache.current;
  }, []);

  // Custom autocomplete search backed by local suburbs JSON
  const performCustomSearch = useCallback(
    _.debounce(async (searchText) => {
      if (!searchText || searchText.length < 1) {
        setFilteredPredictions([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      const list = await ensureSuburbsLoaded();
      const q = searchText.trim().toLowerCase();
      const results = list
        .filter(item => {
          const suburb = String(item.suburb || '').toLowerCase();
          const state = String(item.state || '').toLowerCase();
          const postcode = String(item.postcode || '').toLowerCase();
          if (field?.onlyShowWithPostalCode !== false && !postcode) return false;
          // Permissive matching: startsWith or substring in suburb/state/postcode
          return suburb.startsWith(q) || suburb.includes(q) || state.startsWith(q) || postcode.startsWith(q);
        })
        .slice(0, 10);
      setFilteredPredictions(results);
      setIsSearching(false);
    }, 300),
    [field?.onlyShowWithPostalCode, ensureSuburbsLoaded]
  );
  const processSelectedSuburb = useCallback((item) => {
    try {
      let formattedAddress = {
        AddressName: String(item.suburb || ''),
        Address: '',
        City: String(item.suburb || ''),
        StreetName: '',
        PostalCode: String(item.postcode || ''),
        State: String(item.state || ''),
        Country: 'Australia',
        Latitude: item.lat ?? '',
        Longitude: item.lng ?? ''
      };
      // Create clean suburb format: "Suburb, STATE POSTCODE"
      const parts = [];
      if (formattedAddress.City) parts.push(formattedAddress.City);
      if (formattedAddress.State) parts.push(formattedAddress.State);
      if (formattedAddress.PostalCode) parts.push(formattedAddress.PostalCode);
      formattedAddress.Address = parts.length >= 2 
        ? `${parts[0]}, ${parts[1]}${parts[2] ? ' ' + parts[2] : ''}`
        : formattedAddress.City;

      // Since we're filtering by postal code, this should always pass
      // but keeping validation as failsafe
      if (field?.requirePostalCode !== false && !formattedAddress.PostalCode) {
        setPostalError('Selected address does not include a postal code.');
        return;
      }

      // Validate Australian postal code format
      if (formattedAddress.PostalCode) {
        const ausPostcodeRegex = /^[0-9]{4}$/;
        if (!ausPostcodeRegex.test(String(formattedAddress.PostalCode))) {
          setPostalError('Invalid Australian postal code format.');
          return;
        }
      }

      // Clear any prior error when selection is valid
      if (postalError) setPostalError('');

      formattedAddress.StreetName = formattedAddress.StreetName.trim();
      
      // Update all form fields
      onChange(field.id, formattedAddress.Address);
      onChange('AddressName', formattedAddress.AddressName);
      onChange('StreetAddress', formattedAddress.StreetName);
      onChange('City', formattedAddress.City);
      onChange('PostalCode', formattedAddress.PostalCode);
      onChange('State', formattedAddress.State);
      onChange('Country', formattedAddress.Country);
      onChange('Latitude', formattedAddress.Latitude);
      onChange('Longitude', formattedAddress.Longitude);
      onChange('DeliveryAddress2', formattedAddress.City ? `${formattedAddress.City}, ${formattedAddress.State} ${formattedAddress.PostalCode}, ${formattedAddress.Country}` : formattedAddress.Address);
      onChange('BillingAddress2', formattedAddress.City ? `${formattedAddress.City}, ${formattedAddress.State} ${formattedAddress.PostalCode}, ${formattedAddress.Country}` : formattedAddress.Address);

      // Calculate distance if company coordinates are provided (Haversine)
      if (company?.yardCordinates && formattedAddress.Latitude && formattedAddress.Longitude) {
        const yardCoordinates = getYardCoordinates(company.yardCordinates);
        const position = { lat: Number(formattedAddress.Latitude), lng: Number(formattedAddress.Longitude) };
        const km = haversineKm(yardCoordinates, position);
        if (!isNaN(km)) onChange('Distance', Math.ceil(km));
      }
      
      setInputValue(formattedAddress.Address);
    } catch (error) {
      console.error('Error processing selected place:', error);
    }
  }, [field?.requirePostalCode, field?.id, onChange, company, postalError]);
  // Handle manual selection from filtered results
  const handleSelectPrediction = useCallback((item) => {
    processSelectedSuburb(item);
    setShowSuggestions(false);
  }, [processSelectedSuburb]);



  // Haversine distance calculation in KM
  const haversineKm = (a, b) => {
    if (!a || !b) return NaN;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371; // Earth radius in km
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const s = Math.sin(dLat/2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng/2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
    return R * c;
  };

  const getYardCoordinates = (coordinates) => {
    if (!coordinates) return null;
    
    const [lat, lng] = coordinates.split(",").map(Number);
    return { lat, lng };
  };

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setPostalError('');
    setInputValue(newValue);
    onChange(field.id, newValue);
    
    // Trigger custom search
    if (newValue.length > 0) {
      setShowSuggestions(true);
      performCustomSearch(newValue);
    } else {
      setShowSuggestions(false);
      setFilteredPredictions([]);
    }
  }, [field.id, onChange, performCustomSearch]);

  const getAddressValue = useCallback(() => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.Address) return value.Address;
    return '';
  }, [value]);

  useEffect(() => {
    setInputValue(getAddressValue());
  }, [getAddressValue]);

  const placeholderText = field.placeHolderText || 
    (field?.restrictToSuburbs 
      ? 'Enter suburb name...' 
      : 'Enter an address...');

  // Always use custom dropdown with local JSON
  const useCustomDropdown = true;

  // Attempt to auto-match typed suburb to suburbs list (called on blur)
  const tryAutoMatchFromInput = useCallback(async () => {
    const text = (inputValue || '').trim();
    if (!text) return false;

    try {
      const list = await ensureSuburbsLoaded();
      const q = text.toLowerCase();

      // Exact postcode match
      let match = list.find(item => String(item.postcode || '') === q || String(item.postcode || '') === text);

      // Exact suburb name match
      if (!match) {
        match = list.find(item => (item.suburb || '').toLowerCase() === q);
      }

      // Try "suburb, state" forms
      if (!match && text.includes(',')) {
        const parts = text.split(',').map(p => p.trim().toLowerCase());
        if (parts.length >= 2) {
          const sub = parts[0];
          const st = parts[1];
          match = list.find(item =>
            (item.suburb || '').toLowerCase() === sub &&
            ((item.state || '').toLowerCase() === st || String(item.postcode || '') === st)
          );
        }
      }

      // startsWith fallback
      if (!match) {
        match = list.find(item => (item.suburb || '').toLowerCase().startsWith(q));
      }

      // If a match is found, process it
      if (match) {
        processSelectedSuburb(match);
        setShowSuggestions(false);
        setPostalError('');
        return true;
      }
    } catch (err) {
      console.error('Auto-match error', err);
    }

    return false;
  }, [inputValue, ensureSuburbsLoaded, processSelectedSuburb]);

  return (
    <div className="w-full relative">
      {useCustomDropdown ? (
        // Custom implementation backed by local suburbs JSON
        <div>
          <input
            type="text"
            id={field.id}
            value={inputValue}
            onChange={handleInputChange}
            disabled={!!disabled}
            placeholder={placeholderText}
            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 transition-colors duration-200 ${
              postalError || error 
                ? 'border-red-400 focus:ring-red-500 bg-red-50' 
                : 'border-gray-300 focus:ring-blue-500 bg-white'
            } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            autoComplete="off"
            onFocus={async () => {
              await ensureSuburbsLoaded();
              if (inputValue.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onBlur={(e) => {
              // Delay to allow click on suggestion; then attempt auto-match if needed
              setTimeout(async () => {
                // If postal code is required and the current inputs don't include one, try to auto-match
                if (field?.requirePostalCode !== false) {
                  // If there's already a valid postal code in form state (value might be object), skip
                  const currentPostal = (typeof value === 'object' && value && value.PostalCode) ? value.PostalCode : null;
                  if (!currentPostal) {
                    const matched = await tryAutoMatchFromInput();
                    if (!matched) {
                      setPostalError('Selected address does not include a postal code.');
                    }
                  }
                }
                setShowSuggestions(false);
              }, 150);
            }}
          />
          
          {/* Custom suggestions dropdown */}
          {showSuggestions && (filteredPredictions.length > 0 || isSearching) && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {isSearching && (
                <div className="p-3 text-gray-500 text-sm flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Searching suburbs...
                </div>
              )}
              
              {!isSearching && filteredPredictions.length === 0 && inputValue.length > 2 && (
                <div className="p-3 text-gray-500 text-sm">
                  No suburbs found for "{inputValue}"
                </div>
              )}
              
              {filteredPredictions.map((item) => (
                <div
                  key={`${item.ssc_code}-${item.postcode}`}
                  onClick={() => handleSelectPrediction(item)}
                  className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                >
                  <div className="font-medium text-gray-900">
                    {item.suburb}
                  </div>
                  <div className="text-sm text-gray-600">
                    {item.state}{item.postcode ? ` ${item.postcode}` : ''}, Australia
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
      
      {/* Error display */}
      {(postalError || error || loadErrorMsg) && (
        <div className="mt-2">
          {postalError && (
            <p className="text-sm text-red-600 flex items-center">
              <span className="mr-1">⚠️</span>
              {postalError}
            </p>
          )}
          {error && !postalError && (
            <p className="text-sm text-red-600 flex items-center">
              <span className="mr-1">⚠️</span>
              {error}
            </p>
          )}
          {loadErrorMsg && !postalError && !error && (
            <p className="text-sm text-red-600 flex items-center">
              <span className="mr-1">⚠️</span>
              {loadErrorMsg}
            </p>
          )}
        </div>
      )}
      
      {/* Helper text for suburb search */}
      {field?.restrictToSuburbs && !error && !postalError && (
        <p className="mt-1 text-xs text-gray-500">
          Start typing to search Australian suburbs. Select from the dropdown for accurate postcode information.
        </p>
      )}
    </div>
  );
};

GooglePlaceField.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    placeHolderText: PropTypes.string,
    country: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string)
    ]),
    restrictToSuburbs: PropTypes.bool,
    requirePostalCode: PropTypes.bool,
    onlyShowWithPostalCode: PropTypes.bool
  }).isRequired,
  company: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool
};

GooglePlaceField.defaultProps = {
  field: {
    country: 'au',
    restrictToSuburbs: false,
    requirePostalCode: true,
    onlyShowWithPostalCode: true
  }
};

export { GooglePlaceField };